"use client";

import { useState, useMemo, useEffect } from "react";
import SearchFilters, { FilterState } from "./SearchFilters";
import TransactionCard, { Transaction } from "./TransactionCard";
import TransactionModal from "./TransactionModal";
import Icon from "@/components/ui/AppIcon";
import { createClient } from "@/lib/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_FILTERS: FilterState = {
  query: "",
  area: "All Areas",
  propertyType: "All Types",
  transactionType: "All",
  sortBy: "recent",
};

export default function SearchPageClient() {
  const { user } = useAuth();
  const isAdmin = user?.user_metadata?.role === "admin" || user?.app_metadata?.role === "admin";

  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 12;

  const refresh = () => {
    setPage(0);
    setTransactions([]);
    fetchTransactions(0, true);
  };

  useEffect(() => {
    // Reset and fetch when filters change
    refresh();
  }, [filters]);

  const fetchTransactions = async (pageToFetch: number, isNewSearch: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      const supabase = createClient();

      let query = supabase
        .from("transactions")
        .select("*", { count: "exact" });

      // Server-side filtering
      if (filters.area !== "All Areas") {
        query = query.eq("area", filters.area);
      }
      if (filters.propertyType !== "All Types") {
        query = query.eq("property_type", filters.propertyType);
      }
      if (filters.transactionType !== "All") {
        query = query.eq("transaction_type", filters.transactionType);
      }
      if (filters.query) {
        query = query.or(`street_address.ilike.%${filters.query}%,area.ilike.%${filters.query}%`);
      }

      // Sorting
      if (filters.sortBy === "recent") {
        query = query.order("submitted_date", { ascending: false });
      } else if (filters.sortBy === "price_desc") {
        query = query.order("sale_price", { ascending: false, nullsFirst: false })
          .order("rental_amount", { ascending: false, nullsFirst: false });
      } else if (filters.sortBy === "price_asc") {
        query = query.order("sale_price", { ascending: true, nullsFirst: false })
          .order("rental_amount", { ascending: true, nullsFirst: false });
      }

      // Pagination
      const from = pageToFetch * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.range(from, to);

      const { data, error: fetchError, count } = await query;

      if (fetchError) throw fetchError;

      const newTransactions = data || [];
      setTransactions(prev => isNewSearch ? newTransactions : [...prev, ...newTransactions]);

      // Update hasMore based on total count
      const currentTotal = isNewSearch ? newTransactions.length : transactions.length + newTransactions.length;
      setHasMore(count ? currentTotal < count : false);
    } catch (err: any) {
      console.error("Failed to fetch transactions:", err);
      setError("Failed to load transactions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchTransactions(nextPage);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this transaction permanently?")) return;

    try {
      const supabase = createClient();
      const { error } = await supabase.from("transactions").delete().eq("id", id);
      if (error) throw error;

      // Update local state for immediate feedback
      setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete transaction.");
    }
  };

  return (
    <>
      <div className="space-y-5">
        {/* Filters */}
        <div className="animate-fade-in-up">
          <SearchFilters
            filters={filters}
            onChange={setFilters}
            totalResults={transactions.length}
          />
        </div>

        {/* Initial Loading State */}
        {loading && transactions.length === 0 && (
          <div className="py-20 text-center animate-fade-in">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal mx-auto mb-4"></div>
            <p className="text-muted text-sm">Searching records...</p>
          </div>
        )}

        {/* Error State */}
        {error && transactions.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center animate-fade-in">
            <Icon name="ExclamationTriangleIcon" size={32} className="text-red-500 mx-auto mb-2" />
            <p className="text-red-800 font-semibold mb-1">Error</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={() => fetchTransactions(0, true)}
              className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Results */}
        {!loading && !error && transactions.length === 0 ? (
          <div className="bg-white border border-border rounded-lg p-16 text-center animate-fade-in">
            <Icon name="MagnifyingGlassIcon" size={40} className="text-border mx-auto mb-4" />
            <p className="font-heading font-semibold text-navy mb-1">No transactions found</p>
            <p className="text-sm text-muted">
              Try adjusting your search or filters.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {transactions.map((t, i) => (
                <TransactionCard
                  key={t.id}
                  transaction={t}
                  onClick={setSelected}
                  animationClass={`animate-fade-in-up stagger-${Math.min((i % PAGE_SIZE) + 1, 5)}`}
                  isAdmin={isAdmin}
                  onEdit={setSelected}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {/* Pagination Controls */}
            {hasMore && (
              <div className="flex justify-center pt-8 pb-12">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-teal text-teal font-semibold rounded-lg hover:bg-teal hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed group shadow-sm"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-teal border-t-transparent rounded-full animate-spin group-hover:border-white group-hover:border-t-transparent" />
                  ) : (
                    <Icon name="ArrowPathIcon" size={18} />
                  )}
                  {loading ? "Loading more..." : "Load More Transactions"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      <TransactionModal
        transaction={selected}
        onClose={() => setSelected(null)}
        isAdmin={isAdmin}
        onRefresh={refresh}
      />
    </>
  );
}